import Icon from '@material-ui/core/Icon'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { MUIDataTableColumnDef } from 'mui-datatables'
import React, { Fragment, useState } from 'react'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import DataTable from '../../../../components/DataTable'
import { ConfirmDialog } from '../../../../components/Dialog'
import Panel from '../../../../components/Panel'
import { Insurer_Minimal } from '../../../../helpers/data/models/contract/Insurer'
import { useErrorService } from '../../../../helpers/errors/ErrorService'
import { useAuthUser } from '../../../../hooks/useAuth'
import { useCommonDataLoadedExisting } from '../../../../hooks/useCommonData'
import { useDeleteInsurer } from '../../../../hooks/useMutations'
import useSnackBar from '../../../../hooks/useSnackBar'
import InsurerDialog from './InsurerDialog'

type DialogState =
    | {
          open: true
          insurer: Insurer_Minimal
      }
    | { open: false }

const AEAInsurersList = () => {
    const { insurers } = useCommonDataLoadedExisting()
    const { errorAlert } = useErrorService()
    const auth = useAuthUser()
    const isAdmin = auth.user.userType === EnumUser.admin
    const { openSnack } = useSnackBar()
    const deleteInsurerMutation = useDeleteInsurer()

    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
    const [openEditDialog, setOpenEditDialog] = useState<DialogState>({ open: false })
    const [openDeleteDialog, setOpenDeleteDialog] = useState<DialogState>({
        open: false,
    })
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'title',
            label: 'Nom',
        },
        {
            name: 'phone',
            label: 'Téléphone',
        },
        {
            name: 'email',
            label: 'Email',
        },
    ]

    const addFeatureData = {
        addHandler: () => {
            setOpenAddDialog(true)
        },
        tooltip: `Ajout d'un agent`,
    }

    const onCreated = (insurer: Insurer_Minimal) => {
        openSnack({
            type: 'success',
            message: `Agent ${insurer.title} créé avec succès !`,
        })
    }

    const onEdited = (insurer: Insurer_Minimal) => {
        openSnack({
            message: 'Agent modifié avec succès !',
            type: 'success',
        })
    }

    const onEditClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, insurer: Insurer_Minimal) => {
        event.stopPropagation()
        setOpenEditDialog({ open: true, insurer })
    }

    const onDeleteClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, insurer: Insurer_Minimal) => {
        event.stopPropagation()
        setOpenDeleteDialog({ open: true, insurer })
    }

    const onDeleteConfirmed = async () => {
        if (!openDeleteDialog.open) return

        setDeleteLoading(true)

        try {
            const insurer = openDeleteDialog.insurer

            const response = await deleteInsurerMutation.run({
                id: insurer.id,
            })

            if (response.errors.length > 0) {
                throw "Une erreur est survenue pendant la suppression de l'agent."
            } else {
                openSnack({
                    message: 'Agent supprimé avec succès !',
                    type: 'success',
                })
            }
        } catch (error) {
            errorAlert(error)
        } finally {
            setDeleteLoading(false)
            setOpenDeleteDialog({ open: false })
        }
    }

    const actionsColumn = (dataIndex: number, rowIndex: number) => {
        if (!isAdmin) return null

        const insurer = insurers[dataIndex]

        return (
            <div className="flex items-center">
                <div className="flex-grow"></div>
                <Tooltip title="Modifier l'agent">
                    <IconButton onClick={(event) => onEditClick(event, insurer)}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer l'agent">
                    <IconButton onClick={(event) => onDeleteClick(event, insurer)}>
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    return (
        <Fragment>
            <Panel>
                <DataTable
                    title={'Agents généraux'}
                    data={insurers}
                    columns={columns}
                    showSearch={true}
                    actionsColumn={actionsColumn}
                    addFeature={addFeatureData}
                    options={{
                        sortOrder: {
                            name: 'title',
                            direction: 'asc',
                        },
                    }}
                />
            </Panel>
            <InsurerDialog onDone={onCreated} open={openAddDialog} closeDialog={() => setOpenAddDialog(false)} />
            {openEditDialog.open && (
                <InsurerDialog
                    onDone={onEdited}
                    open={openEditDialog.open}
                    closeDialog={() => setOpenEditDialog({ open: false })}
                    insurer={openEditDialog.insurer}
                />
            )}
            {openDeleteDialog.open && (
                <ConfirmDialog
                    open={openDeleteDialog.open}
                    onCancelled={() => setOpenDeleteDialog({ open: false })}
                    onConfirmed={onDeleteConfirmed}
                    title="Suppression d'un agent"
                    text={`Voulez-vous vraiment supprimer l'agent "${openDeleteDialog.insurer.title}" ?`}
                    loading={deleteLoading}
                />
            )}
        </Fragment>
    )
}

export default AEAInsurersList
